import React from 'react'
import { Tooltip } from '@mui/material'
import { AddRounded, DeleteOutline, EditOutlined, CheckRounded, CloseRounded, ArrowBack, ArrowForward, Search as SearchIcon, UploadFileOutlined } from '@mui/icons-material'
import { useDispatch, useSelector } from '../../store'
import { useAuthEffect } from '../../hook/useAuthEffect'
import {
  deleteBlackList, deleteBlackListIpAddress, deleteBlackListPhones, editBlackList, editBlackListIpAddress, editBlackListPhones,
  fetchBlackList, fetchBlackListIpAddress, fetchBlackListPhones, postBlackList, postBlackListIpAddress, postBlackListPhones,
} from '../../store/blacklist/actionCreator'
import { ConfirmV2 } from '../ConfirmV2'
import { ImportBlacklistV2 } from './ImportBlacklistV2'
import { Kind, normalize, VALID } from './blacklistRules'
import s from './SystemV2.module.css'

/*
 * Blacklists — emails, phones and IP addresses that may not sign up / are
 * blocked. The classic tab's three side-by-side tables and their store
 * actions (/blacklist, /blacklist/phones, /blacklist/ip_address — add,
 * edit, delete; each reloads its list), as one table with a switch.
 * New: the value is checked before it is sent (the classic popups sent
 * anything), the search covers any part, deleting asks first, and a whole
 * list can be imported (ImportBlacklistV2 → POST /blacklist/import).
 *
 * How the backend matches (BlackListService) decides what an entry can be:
 * an email is blocked when it *contains* an entry (so "@domain.com" or
 * "tempmail" work); a phone or an IP when it *starts with* one (so "+7" or
 * "203.0.113." block a whole range). A phone always gets a "+" there, so an
 * entry without one would never match — it is added here.
 */

type Row = { id: number; value: string }

const KINDS: { key: Kind; label: string; placeholder: string; error: string; hint: string }[] = [
  {
    key: 'email', label: 'Emails', placeholder: 'name@example.com, @domain.com or tempmail',
    error: 'At least 3 characters, no spaces', hint: 'Blocks every email that contains it — a whole address, a domain or a word.',
  },
  {
    key: 'phone', label: 'Phones', placeholder: '+380501234567 or +7',
    error: 'A + and digits only', hint: 'Blocks every number that starts with it — a full number or a country / operator code.',
  },
  {
    key: 'ip', label: 'IP addresses', placeholder: '203.0.113.7 or 203.0.113.',
    error: 'Digits, dots (or hex and colons for IPv6), at least 3 characters', hint: 'Blocks every address that starts with it — a full IP or a range like 203.0.113.',
  },
]

const PER_PAGE = 20

export function BlacklistsV2() {
  const dispatch = useDispatch()
  const { blackList } = useSelector((state) => state.blackList)
  const { blackListPhones } = useSelector((state) => state.blackListPhones)
  const { blackListIpAddress } = useSelector((state) => state.blackListIpAddress)

  const [kind, setKind] = React.useState<Kind>('email')
  const [query, setQuery] = React.useState('')
  const [page, setPage] = React.useState(0)
  const [draft, setDraft] = React.useState('')
  const [draftError, setDraftError] = React.useState('')
  const [editing, setEditing] = React.useState<{ id: number; value: string; error?: string } | null>(null)
  const [toDelete, setToDelete] = React.useState<Row | null>(null)
  const [importing, setImporting] = React.useState(false)

  useAuthEffect(() => {
    dispatch(fetchBlackList)
    dispatch(fetchBlackListPhones)
    dispatch(fetchBlackListIpAddress)
  })
  React.useEffect(() => { setPage(0); setEditing(null); setDraftError('') }, [kind, query])

  const rowsOf: Record<Kind, Row[] | null> = {
    email: blackList ? blackList.map((x) => ({ id: x.id, value: x.email })) : null,
    phone: blackListPhones ? blackListPhones.map((x) => ({ id: x.id, value: x.phone })) : null,
    ip: blackListIpAddress ? blackListIpAddress.map((x) => ({ id: x.id, value: x.ipAddress })) : null,
  }
  const rows = rowsOf[kind]
  const meta = KINDS.find((k) => k.key === kind) as typeof KINDS[number]
  const q = query.trim().toLowerCase()
  const list = (rows || []).filter((r) => !q || (r.value || '').toLowerCase().includes(q)).sort((a, b) => b.id - a.id)
  const pages = Math.max(1, Math.ceil(list.length / PER_PAGE))
  const shown = list.slice(page * PER_PAGE, page * PER_PAGE + PER_PAGE)

  function add() {
    const v = normalize(kind, draft)
    if (!VALID[kind](v)) { setDraftError(meta.error); return }
    if ((rows || []).some((r) => (r.value || '').toLowerCase() === v.toLowerCase())) { setDraftError('Already on the list'); return }
    if (kind === 'email') dispatch(postBlackList({ email: v }))
    if (kind === 'phone') dispatch(postBlackListPhones({ phone: v }))
    if (kind === 'ip') dispatch(postBlackListIpAddress({ ipAddress: v }))
    setDraft('')
    setDraftError('')
  }

  function saveEdit() {
    if (!editing) return
    const v = normalize(kind, editing.value)
    if (!VALID[kind](v)) { setEditing({ ...editing, error: meta.error }); return }
    if (kind === 'email') dispatch(editBlackList({ id: editing.id, email: v }))
    if (kind === 'phone') dispatch(editBlackListPhones({ id: editing.id, phone: v }))
    if (kind === 'ip') dispatch(editBlackListIpAddress({ id: editing.id, ipAddress: v }))
    setEditing(null)
  }

  function remove(r: Row) {
    if (kind === 'email') dispatch(deleteBlackList({ id: r.id }))
    if (kind === 'phone') dispatch(deleteBlackListPhones({ id: r.id }))
    if (kind === 'ip') dispatch(deleteBlackListIpAddress({ id: r.id }))
  }

  return (
    <div className={s.section}>
      <div className={s.sectionHead}>
        <div>
          <b>Blacklists</b>
          <span>Emails, phones and IP addresses that are blocked from signing up.</span>
        </div>
      </div>

      <div className={s.toolbar}>
        <div className={s.seg}>
          {KINDS.map((k) => (
            <button key={k.key} type="button" className={kind === k.key ? s.segOn : ''} onClick={() => setKind(k.key)}>
              {k.label}<em>{rowsOf[k.key]?.length ?? '—'}</em>
            </button>
          ))}
        </div>
        <label className={s.search}>
          <SearchIcon sx={{ fontSize: 16 }} />
          <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder={`Search ${meta.label.toLowerCase()}`} />
        </label>
      </div>

      <div className={s.card}>
        <form className={s.addRow} onSubmit={(e) => { e.preventDefault(); add() }}>
          <input className={s.input} value={draft} onChange={(e) => { setDraft(e.target.value); setDraftError('') }} placeholder={meta.placeholder} />
          <button type="submit" className={s.primary} disabled={!draft.trim()}><AddRounded sx={{ fontSize: 17 }} />Add</button>
          <button type="button" className={s.ghostBtn} onClick={() => setImporting(true)}><UploadFileOutlined sx={{ fontSize: 16 }} />Import a list</button>
        </form>
        {draftError ? <div className={s.err}>{draftError}</div> : <div className={s.hint}>{meta.hint}</div>}

        {rows === null ? (
          <div className={s.empty}>Loading…</div>
        ) : !shown.length ? (
          <div className={s.empty}>{q ? 'Nothing matches the search' : `No ${meta.label.toLowerCase()} on the list`}</div>
        ) : (
          <div className={s.rows}>
            {shown.map((r) => (
              <div key={r.id} className={s.row}>
                {editing?.id === r.id ? (
                  <form className={s.editRow} onSubmit={(e) => { e.preventDefault(); saveEdit() }}>
                    <input className={s.input} value={editing.value} autoFocus onChange={(e) => setEditing({ id: r.id, value: e.target.value })} onKeyDown={(e) => { if (e.key === 'Escape') setEditing(null) }} />
                    <Tooltip title="Save"><button type="submit" className={s.iconOk} aria-label="Save"><CheckRounded sx={{ fontSize: 17 }} /></button></Tooltip>
                    <Tooltip title="Cancel"><button type="button" className={s.iconGhost} onClick={() => setEditing(null)} aria-label="Cancel"><CloseRounded sx={{ fontSize: 17 }} /></button></Tooltip>
                    {editing.error && <span className={s.err}>{editing.error}</span>}
                  </form>
                ) : (
                  <>
                    <span className={s.value}>{r.value}</span>
                    <Tooltip title="Edit"><button type="button" className={s.iconGhost} onClick={() => setEditing({ id: r.id, value: r.value })} aria-label="Edit"><EditOutlined sx={{ fontSize: 16 }} /></button></Tooltip>
                    <Tooltip title="Remove from the list"><button type="button" className={s.iconDanger} onClick={() => setToDelete(r)} aria-label="Remove"><DeleteOutline sx={{ fontSize: 16 }} /></button></Tooltip>
                  </>
                )}
              </div>
            ))}
          </div>
        )}

        {pages > 1 && (
          <div className={s.pager}>
            <span>{page * PER_PAGE + 1}–{Math.min(list.length, (page + 1) * PER_PAGE)} of {list.length}</span>
            <div className={s.pagerBtns}>
              <button type="button" className={s.iconGhost} disabled={page === 0} onClick={() => setPage(page - 1)} aria-label="Previous page"><ArrowBack sx={{ fontSize: 16 }} /></button>
              <button type="button" className={s.iconGhost} disabled={page >= pages - 1} onClick={() => setPage(page + 1)} aria-label="Next page"><ArrowForward sx={{ fontSize: 16 }} /></button>
            </div>
          </div>
        )}
      </div>

      {importing && (
        <ImportBlacklistV2
          initialKind={kind}
          existing={{
            email: (rowsOf.email || []).map((r) => r.value),
            phone: (rowsOf.phone || []).map((r) => r.value),
            ip: (rowsOf.ip || []).map((r) => r.value),
          }}
          onClose={() => setImporting(false)}
          onDone={(kinds) => {
            if (kinds.includes('email')) dispatch(fetchBlackList)
            if (kinds.includes('phone')) dispatch(fetchBlackListPhones)
            if (kinds.includes('ip')) dispatch(fetchBlackListIpAddress)
          }}
        />
      )}
      {toDelete && (
        <ConfirmV2
          header={`Remove ${toDelete.value} from the blacklist?`}
          onYes={() => remove(toDelete)}
          onClose={() => setToDelete(null)}
        />
      )}
    </div>
  )
}
